import classnames from 'classnames';
import React, { FunctionComponent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DonationAmountSelector.scss';

type CustomDonationAmountTextInputProps = {
  errorText?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearClick: () => void;
};

export const CustomDonationAmountTextInput: FunctionComponent<CustomDonationAmountTextInputProps> = ({
  errorText,
  onChange,
  onBlur,
  onClearClick,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });

  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
    }
  }, []);

  const handleClearClick = () => {
    onClearClick();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      onClearClick();
    }
  };

  return (
    <div
      className={classnames(
        'jg-form-group jg-space-mbsm',
        styles.customAmountWrapper,
      )}
    >
      <label htmlFor="customDonationAmount" className="jg-sr-only">
        {t('donationAmount.customDonationAmount.label')}
      </label>
      <input
        data-qa="customDonationAmount"
        id="customDonationAmount"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        className={classnames('jg-form-control', styles.customAmountInput, {
          'jg-form-control--error': errorText,
        })}
        ref={ref}
        placeholder={t('donationAmount.customDonationAmount.placeholder')}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      />
      <span
        className={classnames(
          'jg-text--large',
          styles.customAmountCurrencySymbol,
        )}
        aria-hidden="true"
      >
        £{/* '£' hard-coded during test as only enabled for GBP pages */}
      </span>
      <button
        type="button"
        className={classnames('jg-text--large', styles.customAmountClose)}
        onClick={handleClearClick}
      >
        <span aria-hidden="true">
          {t('donationAmount.customDonationAmountClear.label')}
        </span>
        <span className="jg-sr-only">
          {t('donationAmount.customDonationAmountClear.screenReaderLabel')}
        </span>
      </button>
      {errorText && (
        <span
          role="alert"
          className={classnames(
            'jg-form-error-copy jg-text--small jg-space-pvn',
            styles.amountErrorMessage,
          )}
        >
          {errorText}
        </span>
      )}
    </div>
  );
};
